import React, { createContext, useContext, useLayoutEffect, useState } from 'react';

/**
 * User-selectable interface background tint.
 *
 * The whole app canvas (shell, pages, chat reading surface) reads its
 * background from the CSS variable `--app-bg`. This context is the single
 * place that variable is written, persisting the choice to localStorage so a
 * user's comfortable color survives reloads.
 *
 * Values are deliberately soft 50-level tints: light enough that body text
 * (gray-800/900) keeps an AA contrast ratio on every option, while still
 * giving a perceptible, eye-friendly shift. `swatch` is the saturated brand
 * hue used only for the picker chip — never as a background.
 */
export interface BgOption {
  id: string;
  /** Hebrew label shown next to the swatch. */
  label: string;
  /** The soft canvas background tint (`--app-bg`). */
  value: string;
  /** Saturated color for the picker swatch dot. */
  swatch: string;
  /** Solid accent used for primary buttons / icon chips (`--accent`). */
  accent: string;
  /** Darker accent for hover (`--accent-hover`). */
  accentHover: string;
  /** Three dark stops for the login hero gradient (top-left → bottom-right). */
  hero: [string, string, string];
}

export const BG_OPTIONS: BgOption[] = [
  {
    id: 'default', label: 'כחול', value: '#f9fafb', swatch: '#2563eb',
    accent: '#2563eb', accentHover: '#1d4ed8', hero: ['#0f172a', '#1e293b', '#1e3a8a'],
  },
  {
    id: 'red', label: 'אדום', value: '#fef2f2', swatch: '#ef4444',
    accent: '#dc2626', accentHover: '#b91c1c', hero: ['#450a0a', '#7f1d1d', '#881337'],
  },
  {
    id: 'orange', label: 'כתום', value: '#fff7ed', swatch: '#f97316',
    accent: '#ea580c', accentHover: '#c2410c', hero: ['#431407', '#7c2d12', '#78350f'],
  },
  {
    id: 'purple', label: 'סגול', value: '#faf5ff', swatch: '#8b5cf6',
    accent: '#7c3aed', accentHover: '#6d28d9', hero: ['#2e1065', '#4c1d95', '#581c87'],
  },
  {
    id: 'green', label: 'ירוק', value: '#f0fdf4', swatch: '#22c55e',
    accent: '#16a34a', accentHover: '#15803d', hero: ['#052e16', '#14532d', '#064e3b'],
  },
  {
    id: 'teal', label: 'טורקיז', value: '#f0fdfa', swatch: '#14b8a6',
    accent: '#0d9488', accentHover: '#0f766e', hero: ['#042f2e', '#134e4a', '#164e63'],
  },
];

// Full primary ramps (50→900) as RGB channels, per palette. ThemeContext
// writes these to --p-50…--p-900 so Tailwind's `primary-*` re-colors the whole
// app. The shade order matches SHADES below.
const SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;
const RAMPS: Record<string, string[]> = {
  default: ['239 246 255', '219 234 254', '191 219 254', '147 197 253', '96 165 250', '59 130 246', '37 99 235', '29 78 216', '30 64 175', '30 58 138'],
  red: ['254 242 242', '254 226 226', '254 202 202', '252 165 165', '248 113 113', '239 68 68', '220 38 38', '185 28 28', '153 27 27', '127 29 29'],
  orange: ['255 247 237', '255 237 213', '254 215 170', '253 186 116', '251 146 60', '249 115 22', '234 88 12', '194 65 12', '154 52 18', '124 45 18'],
  purple: ['245 243 255', '237 233 254', '221 214 254', '196 181 253', '167 139 250', '139 92 246', '124 58 237', '109 40 217', '91 33 182', '76 29 149'],
  green: ['240 253 244', '220 252 231', '187 247 208', '134 239 172', '74 222 128', '34 197 94', '22 163 74', '21 128 61', '22 101 52', '20 83 45'],
  teal: ['240 253 250', '204 251 241', '153 246 228', '94 234 212', '45 212 191', '20 184 166', '13 148 136', '15 118 110', '17 94 89', '19 78 74'],
};

// Dark chrome ramps (50→950) as RGB channels, per palette. ThemeContext writes
// these to --s-50…--s-950 so Tailwind's `sidebar-*` re-tints the Header,
// Sidebar and dropdowns toward the chosen hue. Each ramp keeps the slate
// lightness of every shade and only rotates the hue, so text/background
// contrast in the chrome stays at parity with the historical slate look.
// `default` is exactly slate — first paint is unchanged.
const CHROME_SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;
const CHROME_RAMPS: Record<string, string[]> = {
  default: ['248 250 252', '241 245 249', '226 232 240', '203 213 225', '148 163 184', '100 116 139', '71 85 105', '51 65 85', '30 41 59', '15 23 42', '2 6 23'],
  red: ['252 248 248', '249 241 241', '240 226 226', '225 203 203', '184 148 148', '139 100 100', '105 71 71', '85 51 51', '59 30 30', '42 15 15', '23 2 2'],
  orange: ['252 249 248', '249 244 241', '240 231 226', '225 211 203', '184 161 148', '139 114 100', '105 83 71', '85 63 51', '59 40 30', '42 24 15', '23 9 2'],
  purple: ['250 248 252', '244 241 249', '231 226 240', '211 203 225', '162 148 184', '115 100 139', '84 71 105', '64 51 85', '41 30 59', '25 15 42', '10 2 23'],
  green: ['248 252 250', '241 249 244', '226 240 232', '203 225 212', '148 184 163', '100 139 116', '71 105 85', '51 85 65', '30 59 42', '15 42 26', '2 23 11'],
  teal: ['248 252 252', '241 249 248', '226 240 239', '203 225 223', '148 184 181', '100 139 136', '71 105 102', '51 85 82', '30 59 57', '15 42 40', '2 23 21'],
};

const STORAGE_KEY = 'ui.bgColor';
const DEFAULT_ID = 'default';

const readPersistedId = (): string => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && BG_OPTIONS.some((o) => o.id === raw)) return raw;
  } catch {
    /* localStorage may be blocked (Safari private mode) — fall through */
  }
  return DEFAULT_ID;
};

// The agent persona is no longer user-selectable — each conversation is bound
// to a domain. Drop the stale persisted pick so it can't resurface.
try {
  window.localStorage.removeItem('ui.agent');
} catch {
  /* localStorage may be blocked — ignore */
}

const applyBg = (id: string) => {
  // Fall back to the DEFAULT_ID option (not positional [0]) so the default
  // stays correct even if BG_OPTIONS is reordered; bail out safely if neither
  // the id nor the default exists rather than throwing on undefined.
  const opt =
    BG_OPTIONS.find((o) => o.id === id) ?? BG_OPTIONS.find((o) => o.id === DEFAULT_ID);
  if (!opt) return;
  try {
    const root = document.documentElement.style;
    root.setProperty('--app-bg', opt.value);
    // Accent palette — re-colors the interface (primary buttons, icon chips,
    // the login hero gradient). Exposed as CSS vars so any surface can opt in.
    root.setProperty('--accent', opt.accent);
    root.setProperty('--accent-hover', opt.accentHover);
    root.setProperty('--hero-from', opt.hero[0]);
    root.setProperty('--hero-via', opt.hero[1]);
    root.setProperty('--hero-to', opt.hero[2]);
    // Full primary ramp → Tailwind `primary-*` recolors the whole app.
    const ramp = RAMPS[opt.id] ?? RAMPS[DEFAULT_ID];
    SHADES.forEach((shade, i) => root.setProperty(`--p-${shade}`, ramp[i]));
    // Dark chrome ramp → Tailwind `sidebar-*` re-tints the Header/Sidebar/
    // dropdowns toward the chosen hue.
    const chrome = CHROME_RAMPS[opt.id] ?? CHROME_RAMPS[DEFAULT_ID];
    CHROME_SHADES.forEach((shade, i) => root.setProperty(`--s-${shade}`, chrome[i]));
  } catch {
    /* SSR / no DOM — ignore */
  }
};

interface ThemeContextValue {
  bgColor: string;
  setBgColor: (id: string) => void;
  options: BgOption[];
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [bgColor, setBgColorState] = useState<string>(readPersistedId);

  // useLayoutEffect so the variable is written before paint — avoids a flash
  // of the default tint on first render / reload.
  useLayoutEffect(() => {
    applyBg(bgColor);
  }, [bgColor]);

  const setBgColor = (id: string) => {
    const next = BG_OPTIONS.some((o) => o.id === id) ? id : DEFAULT_ID;
    setBgColorState(next);
    applyBg(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  };

  return (
    <ThemeContext.Provider value={{ bgColor, setBgColor, options: BG_OPTIONS }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
};
